/* @flow */
import * as React from 'react';
import { useTranslation } from 'react-i18next';
import {
    Dialog,
    DialogHeader,
    DialogFooter,
    DialogContent,
    Button,
    Heading,
    Box,
    TextField
} from '@locus-taxy/neo-ui';

type ChangeReasonDialogProps = {
    isOpen: boolean,
    onSave: (reason: string) => void,
    onDismiss: () => void
};

const ChangeReasonDialog = ({
    isOpen,
    onSave,
    onDismiss
}: ChangeReasonDialogProps): React.Node => {
    const { t } = useTranslation('platform');
    const [reason, setReason] = React.useState('');

    const onSaveAction = () => {
        onSave(reason);
    };

    return (
        <Dialog
            isOpen={isOpen}
            sticky
            onDismiss={onDismiss}
            width={'small'}
            dismissOnBackdrop={false}
            dismissOnEscape={false}
        >
            <DialogHeader>
                <Heading truncated>
                    {t('settings.common.reasonForChange.label')}
                </Heading>
            </DialogHeader>
            <DialogContent background="grey">
                <Box>
                    <TextField
                        label={t('settings.common.reasonForChange.label')}
                        value={reason}
                        fullWidth
                        required
                        onChange={value => setReason(value)}
                        helperText={t(
                            'settings.common.reasonForChange.description'
                        )}
                    />
                </Box>
            </DialogContent>
            <DialogFooter>
                <Button
                    appearance="solid"
                    palette="primary"
                    onClick={onSaveAction}
                    disabled={!reason}
                >
                    {t('save')}
                </Button>
            </DialogFooter>
        </Dialog>
    );
};

type Props = {
    label?: string,
    showReasonModal: boolean,
    onSave: (reason?: string) => void,
    isSaving?: boolean,
    disabled?: boolean
};

const SaveWithReasonButton = ({
    label,
    showReasonModal,
    onSave,
    isSaving = false,
    disabled = false
}: Props): React.Node => {
    const { t } = useTranslation('platform');
    const [isOpen, setIsOpen] = React.useState(false);

    const onSaveAction = (reason: string) => {
        if (reason) {
            onSave(reason);
            setIsOpen(false);
        }
    };

    const onDismiss = () => {
        setIsOpen(false);
    };

    return (
        <>
            <Button
                appearance="solid"
                palette="primary"
                onClick={() => {
                    if (showReasonModal) {
                        setIsOpen(true);
                    } else {
                        onSave();
                    }
                }}
                disabled={disabled}
                isLoading={isSaving}
            >
                {label || t('save')}
            </Button>
            <ChangeReasonDialog
                isOpen={isOpen}
                onSave={onSaveAction}
                onDismiss={onDismiss}
            />
        </>
    );
};

export default SaveWithReasonButton;
